import {
  Typography,
  Grid,
  Box,
  Link,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ShareLocationIcon from "@mui/icons-material/ShareLocation";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { centros } from "./centros";
import { ICON_BASE_PATH, IMAGE_BASE_PATH } from "../../config/paths";
import ButtonsFilterServico from "../../components/buttons/ButtonsFilterServico";
import ButtonsFilter from "../../components/buttons/ButtonsFilter";
import { useJsonLd } from "../../components/seo/useJsonLd";
import { useSeo } from "../../components/seo/useSeo";


interface CentrosBaseProps {
  tipo: "HOTEL" | "REVENDEDOR" | "ESCOLA" | "SERVICO";
  centroNome: string; // chave ou texto (você está passando texto e usando t())
  centroDescricao: string; // já vem pronto (string)
  excludedCountries?: string[];
}

function toAbsoluteUrl(urlOrPath: string) {
  try {
    // já absoluta?
    return new URL(urlOrPath).toString();
  } catch {
    // relativa → absoluta
    const base = window.location.origin;
    const path = urlOrPath.startsWith("/") ? urlOrPath : `/${urlOrPath}`;
    return new URL(path, base).toString();
  }
}

function stripProtocolAndWww(url: string) {
  return url.replace(/^https?:\/\/(www\.)?/i, "");
}

function normalizePhoneToTel(phone: string) {
  const cleaned = phone.replace(/[^\d+]/g, "");
  return cleaned ? `tel:${cleaned}` : undefined;
}

export default function CentrosBase(props: CentrosBaseProps) {
  const { tipo, centroNome, centroDescricao, excludedCountries } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation();

  const [filtroServico, setFiltroServico] = useState("TODOS");
  const [filtro, setFiltro] = useState("LATAM");

  const centrosFiltrados = useMemo(() => {
    let lista = centros.filter((centro) => {
      switch (tipo) {
        case "HOTEL":
          return centro.hotelresto;
        case "REVENDEDOR":
          return centro.revendedor;
        case "ESCOLA":
          return centro.escola;
        case "SERVICO":
          return centro.servico;
      }
    });

    lista = lista.filter((centro) => {
      if (filtroServico === "TODOS") return true;
      return Boolean(centro?.tipo?.includes(filtroServico));
    });

    return lista.filter((centro) => {
      if (filtro === t("LATAM")) return true;
      return centro.pais.substring(0, 2) === filtro.substring(0, 2);
    });
  }, [tipo, filtroServico, filtro, t]);

  const manageFlagIcons = useCallback((pais: string) => {
    const icon =
      pais === "URUGUAI"
        ? "UruguaiIcon.svg"
        : pais === "ARGENTINA"
        ? "ArgentinaIcon.svg"
        : pais === "PERU"
        ? "PeruIcon.svg"
        : pais === "CHILE"
        ? "ChileIcon.svg"
        : "BrazilIcon.svg";
    return (
      <img
        src={`${ICON_BASE_PATH}${icon}`}
        alt={t("CentrosBase.flagOf", { country: t(`Countries.${pais}`) })}
        style={{ width: 36, height: 36 }}
        loading="lazy"
      />
    );
  }, [t]);

  const manageFlagColors = useCallback((pais: string) => {
    switch (pais) {
      case "ALEMANHA":
        return "linear-gradient(90deg, #000000 0%, #DD0000 50%, #FFCC00 60%)";
      case "ARGENTINA":
        return "linear-gradient(90deg, #74ACDF 0%, #FFFFFF 50%, #74ACDF 100%)";
      case "URUGUAI":
        return "linear-gradient(90deg, #FFFFFF 0%, #3A75C4 50%, #FFFFFF 100%)";
      case "PERU":
        return "linear-gradient(90deg, #D91023 0%, #FFFFFF 50%, #D91023 100%)";
      default:
        return "linear-gradient(90deg, #FFDE00 0%, #009B3A 70%, #0033A0 100%)";
    }
  }, []);

  const screenImage = useMemo(() => {
    switch (tipo) {
      case "HOTEL":
        return `${IMAGE_BASE_PATH}centros/hoteis/${isMobile ? "HotelBanner2.avif" : "HotelBanner.avif"}`;
      case "REVENDEDOR":
        return `${IMAGE_BASE_PATH}centros/revendedores/RevendedorBanner.avif`;
      case "ESCOLA":
        return `${IMAGE_BASE_PATH}centros/escolas/${isMobile ? "EscolasBannerMobile.avif" : "EscolasBanner.avif"}`;
      case "SERVICO":
        return `${IMAGE_BASE_PATH}centros/servicos/ServicoBanner.avif`;
    }
  }, [tipo, isMobile]);

  /* ===========================
   * SEO (title/desc/canonical/ogimage)
   * =========================== */

  const pageH1 = useMemo(() => t(centroNome), [t, centroNome]);

  const canonical = useMemo(() => {
    const u = new URL(window.location.href);
    u.search = "";
    u.hash = "";
    return u.toString();
  }, []);

  const ogImageAbs = useMemo(() => toAbsoluteUrl(screenImage), [screenImage]);

  const locale = useMemo(() => {
    const lang = i18n.language?.toLowerCase() || "pt";
    if (lang.startsWith("pt")) return "pt_BR";
    if (lang.startsWith("es")) return "es_ES";
    return "en_US";
  }, [i18n.language]);

  const title = useMemo(() => `${pageH1} | Core Latam`, [pageH1]);

  useSeo({
    title,
    description: centroDescricao,
    canonical,
    image: ogImageAbs,
    type: "website",
    noindex: false,
    locale,
  });

  /* ===========================
   * JSON-LD (Breadcrumb + CollectionPage + ItemList)
   * =========================== */

  const breadcrumbLd = useMemo(() => {
    const base = window.location.origin;
    const path = window.location.pathname;

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: new URL("/", base).toString(),
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Centros",
          item: new URL("/centros", base).toString(),
        },
        {
          "@type": "ListItem",
          position: 3,
          name: pageH1,
          item: new URL(path, base).toString(),
        },
      ],
    };
  }, [pageH1]);

  const itemListLd = useMemo(() => {
    const itemListElement = centrosFiltrados.map((centro, index) => {
      const url = centro.site || centro.googleMapsUrl || undefined;

      const sameAs: string[] = [];
      if (centro.site) sameAs.push(centro.site);
      if (centro.instagram) {
        const handle = centro.instagram.replace("@", "");
        sameAs.push(`https://www.instagram.com/${handle}/`);
      }

      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "LocalBusiness",
          name: centro.nome,
          url,
          image: centro.imagem ? toAbsoluteUrl(centro.imagem) : undefined,
          telephone: centro.telefone || undefined,
          email: centro.email || undefined,
          address: {
            "@type": "PostalAddress",
            addressCountry: centro.pais,
            addressLocality: centro.local,
          },
          sameAs: sameAs.length ? sameAs : undefined,
        },
      };
    });

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: pageH1,
      description: centroDescricao,
      numberOfItems: centrosFiltrados.length,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement,
    };
  }, [centrosFiltrados, pageH1, centroDescricao]);

  const collectionPageLd = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: pageH1,
      description: centroDescricao,
      url: canonical,
      inLanguage: locale,
      isPartOf: {
        "@type": "WebSite",
        name: "Core Latam",
        url: window.location.origin,
      },
    };
  }, [pageH1, centroDescricao, canonical, locale]);

  useJsonLd(`centros-${tipo.toLowerCase()}-breadcrumb`, breadcrumbLd);
  useJsonLd(`centros-${tipo.toLowerCase()}-collectionpage`, collectionPageLd);
  useJsonLd(`centros-${tipo.toLowerCase()}-itemlist`, itemListLd);

  /* ===========================
   * IDs A11y (SEO + acessibilidade)
   * =========================== */
  const titleId = `centros-title-${tipo.toLowerCase()}`;
  const descId = `centros-desc-${tipo.toLowerCase()}`;

  return (
    <Box component="main" aria-labelledby={titleId} aria-describedby={descId} sx={{ backgroundColor: "white" }}>
      <Grid container spacing={4} justifyContent="center">
        {/* HERO */}
        <Grid size={12} sx={{ height: { xs: "80vh", sm: "60vh" } }}>
          <Box sx={{ position: "relative", height: "100%", overflow: "hidden" }}>
            <img
              src={screenImage}
              alt=""
              aria-hidden="true"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              loading="eager"
            />
          </Box>
        </Grid>

        {/* H1 */}
        <Grid size={12} component="header">
          <Typography
            id={titleId}
            component="h1"
            fontSize={{ xs: 45, sm: 65, md: 85, lg: 100, xl: 100 }}
            sx={{
              color: "black",
              textAlign: "center",
              fontFamily: "PFDinTextPro-Thin, sans-serif",
            }}
          >
            {pageH1}
          </Typography>
        </Grid>

        {/* Descrição */}
        <Grid
          size={12}
          sx={{
            px: { xs: 5, sm: 10, md: 15, lg: 25, xl: 25 },
            mb: 3,
          }}
        >
          <Typography
            id={descId}
            component="p"
            variant="body1"
            fontSize={{ xs: 14, sm: 21, md: 21, lg: 21, xl: 21 }}
            sx={{
              color: "black",
              textAlign: "center",
              fontFamily: "Helvetica,Arial,Verdana,sans-serif",
              fontWeight: 100,
            }}
          >
            {centroDescricao}
          </Typography>
        </Grid>

        {/* Filtros */}
        {tipo === "SERVICO" && (
          <Grid size={12} textAlign="center" component="section" aria-label={t("CentrosBase.filterServices")}>
            <ButtonsFilterServico selected={filtroServico} setSelected={setFiltroServico} />
          </Grid>
        )}

        <Grid size={12} textAlign="center" component="section" aria-label={t("CentrosBase.filterCountry")}>
          <ButtonsFilter selected={filtro} setSelected={setFiltro} excludedCountries={excludedCountries} />
        </Grid>

        {/* Lista */}
        <Grid
          container
          component="section"
          aria-label={t("CentrosBase.listOf", { name: pageH1 })}
          spacing={4}
          justifyContent="center"
          sx={{ px: { xs: 2, sm: 10, md: 10, lg: 10, xl: 10 }, pb: 6 }}
        >
          {centrosFiltrados.map((centro, index) => {
            const cardImageAlt = t("CentrosBase.centerImageAlt", {
              name: centro.nome,
              city: centro.local,
              country: t(`Countries.${centro.pais}`),
            });

            const instagramUrl = centro.instagram
              ? `https://www.instagram.com/${centro.instagram.replace("@", "")}/`
              : undefined;

            const telHref = centro.telefone ? normalizePhoneToTel(centro.telefone) : undefined;
            const mailHref = centro.email ? `mailto:${centro.email}` : undefined;
            
            return (
              <Grid
                size={{
                xs:12,
                sm:6,
                md:4,
                lg:centrosFiltrados.length <= 3 ? 4 : 3,
                xl:centrosFiltrados.length <= 3 ? 4 : 3
                }}
                key={`${centro.nome}-${index}`}
                sx={{ minWidth: 300, maxWidth: 350 }}
              >
                <Box
                  component="article"
                  aria-label={centro.nome}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  {/* Imagem */}
                  <Box sx={{ width: "100%", maxWidth: "100%", display: "inline-block" }}>
                    <Box
                      component="img"
                      sx={{ height: 280, width: "100%", objectFit: "cover", display: "block" }}
                      alt={cardImageAlt}
                      src={centro.imagem}
                      loading="lazy"
                    />
                    <Box sx={{ background: manageFlagColors(centro.pais), height: "4px" }} />
                  </Box>

                  {/* Cabeçalho do card */}
                  <Box display="flex" flexDirection="row" justifyContent="center" mb={2} mt={2}>
                    <Box display="flex" flexDirection="column" alignItems="end">
                      <Box display="flex" flexDirection="row" alignItems="center">
                        {manageFlagIcons(centro.pais)}
                        <Typography
                          component="h2"
                          variant="h6"
                          sx={{
                            fontFamily: "Lato, sans-serif",
                            fontWeight: 400,
                            color: "black",
                            ml: 1,
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            mb: 0,
                          }}
                        >
                          {centro.nome}
                        </Typography>
                      </Box>

                      {centro.googleMapsUrl ? (
                        <Typography
                          component="p"
                          variant="body2"
                          sx={{
                            fontFamily: "Helvetica,Arial,Verdana,sans-serif",
                            fontWeight: 500,
                            color: "grey",
                            mt: -1.4,
                          }}
                        >
                          <Link
                            href={centro.googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: "black",
                              textDecorationColor: "#FFDE00",
                              "&:hover": { color: "#FFDE00" },
                            }}
                            aria-label={t("CentrosBase.viewOnMap", { name: centro.nome })}
                          >
                            {t("CentrosBase.viewMap")}
                          </Link>
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>

                  {/* Site */}
                  {centro.site ? (
                    <Box display="flex" flexDirection="row" justifyContent="left">
                      <LanguageIcon />
                      <Typography
                        component="p"
                        variant="body2"
                        sx={{
                          mt: 0.3,
                          fontFamily: "Helvetica,Arial,Verdana,sans-serif",
                          fontWeight: 500,
                          color: "black",
                          ml: 0.5,
                        }}
                      >
                        <Link
                          href={centro.site}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "black", textDecorationColor: "#FFDE00" }}
                          aria-label={t("CentrosBase.openWebsite", { name: centro.nome })}
                        >
                          {stripProtocolAndWww(centro.site)}
                        </Link>
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ height: "24px" }} />
                  )}

                  {/* Local */}
                  <Box display="flex" flexDirection="row" justifyContent="left">
                    <ShareLocationIcon />
                    <Typography
                      component="p"
                      variant="body2"
                      sx={{
                        mt: 0.3,
                        fontFamily: "Helvetica,Arial,Verdana,sans-serif",
                        fontWeight: 500,
                        color: "black",
                        ml: 0.5,
                      }}
                    >
                      {centro.local}
                    </Typography>
                  </Box>

                  {/* Telefone */}
                  {centro.telefone ? (
                    <Box display="flex" flexDirection="row" justifyContent="left">
                      <PhoneOutlinedIcon />
                      <Typography
                        component="p"
                        variant="body2"
                        sx={{
                          mt: 0.3,
                          fontFamily: "Helvetica,Arial,Verdana,sans-serif",
                          fontWeight: 500,
                          color: "black",
                          ml: 0.5,
                        }}
                      >
                        {telHref ? (
                          <Link
                            href={telHref}
                            underline="none"
                            sx={{ color: "black" }}
                            aria-label={t("CentrosBase.call", { name: centro.nome })}
                          >
                            {centro.telefone}
                          </Link>
                        ) : (
                          centro.telefone
                        )}
                      </Typography>
                    </Box>
                  ) : null}

                  {/* Email */}
                  {centro.email ? (
                    <Box display="flex" flexDirection="row" justifyContent="left">
                      <MailOutlineIcon />
                      <Typography
                        component="p"
                        variant="body2"
                        sx={{
                          mt: 0.3,
                          fontFamily: "Helvetica,Arial,Verdana,sans-serif",
                          fontWeight: 500,
                          color: "black",
                          ml: 0.5,
                        }}
                      >
                        {mailHref ? (
                          <Link
                            href={mailHref}
                            underline="none"
                            sx={{ color: "black" }}
                            aria-label={t("CentrosBase.sendEmail", { name: centro.nome })}
                          >
                            {centro.email}
                          </Link>
                        ) : (
                          centro.email
                        )}
                      </Typography>
                    </Box>
                  ) : null}

                  {/* Instagram */}
                  <Box
                    component="footer"
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    sx={{
                      backgroundColor: "#FFDE00",
                      mt: 2.5,
                      py: 0.5,
                      px: 2,
                      mx: "auto",
                      borderRadius: 1,
                      width: "100%",
                    }}
                  >
                    <InstagramIcon sx={{ color: "black", mt: 0.5 }} />
                    {instagramUrl ? (
                      <Link
                        href={instagramUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{ textDecoration: "none" }}
                        aria-label={t("CentrosBase.openInstagram", { name: centro.nome })}
                      >
                        <Typography
                          component="span"
                          variant="body1"
                          sx={{
                            fontFamily: "Helvetica,Arial,Verdana,sans-serif",
                            fontWeight: "bold",
                            color: "black",
                            ml: "8px",
                          }}
                        >
                          {centro.instagram}
                        </Typography>
                      </Link>
                    ) : (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ ml: 1, color: "black", fontFamily: "Helvetica,Arial,Verdana,sans-serif" }}
                      >
                        Instagram
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Box>
  );
}
